import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Tab, Tabs, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import { useAuth } from '../AuthContext';
import Constants from '../Constants';
import '../App.css';

const c_BillingModePerGeneration = 'per_generation';
const c_BillingModePerSecond = 'per_second';

const TTS_PROVIDERS = [
	{ value: 'elevenlabs', label: 'ElevenLabs', billing_mode: c_BillingModePerGeneration, default_cost_per_gen: 0.03 },
	{ value: 'resembleai', label: 'Resemble AI', billing_mode: c_BillingModePerSecond, default_cost_per_second: 0.0005, default_seconds_per_gen: 5 },
];

const get_provider = (provider_value) => {
	return TTS_PROVIDERS.find(function(provider) {
		return provider.value === provider_value;
	});
};

const fmt = (n, decimals = 2) => {
	return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};


const get_credits_from_plan = (plan) => {
	if(!plan.features || !Array.isArray(plan.features) || plan.features.length === 0) return 0;
	const f = plan.features.find(function(feature) { return feature.code === 'MONTHLY_TRAINING_CREDITS'; });
	if(!f) return 0;
	return parseFloat(f.limit_value) || 0;
};

function PerGenerationModel({ plans }) {
	const [tts_provider, set_tts_provider] = useState('resembleai');
	const [cost_per_gen_input, set_cost_per_gen_input] = useState('0.03');
	const [cost_per_second_input, set_cost_per_second_input] = useState('0.0005');
	const [seconds_per_gen_input, set_seconds_per_gen_input] = useState('5');
	const [runs_per_call_input, set_runs_per_call_input] = useState('25');
	const selected_provider = get_provider(tts_provider);
	const billing_mode = selected_provider ? selected_provider.billing_mode : c_BillingModePerGeneration;
	const tts_provider_name = selected_provider ? selected_provider.label : tts_provider;
	const cost_per_second = parseFloat(cost_per_second_input) || 0;
	const seconds_per_gen = parseFloat(seconds_per_gen_input) || 0;
	const cost_per_gen = (billing_mode === c_BillingModePerSecond) ? (cost_per_second * seconds_per_gen) : (parseFloat(cost_per_gen_input) || 0);
	const runs_per_call = parseInt(runs_per_call_input) || 0;
	const runs_per_call_column_width = (billing_mode === c_BillingModePerSecond) ? 2 : 3;
	const [plan_inputs, set_plan_inputs] = useState({});

	useEffect(function() {
		const inputs = {};
		const ordered_plans = plans.slice().sort(function(first_plan, second_plan) {
			return first_plan.sort_order - second_plan.sort_order;
		});
		ordered_plans.forEach(function(plan) {
			inputs[plan.id] = {
				name: plan.name,
				billing_interval: plan.billing_interval,
				price: (plan.price_in_cents / 100),
				credits: get_credits_from_plan(plan),
				subscribers: 0,
			};
		});
		set_plan_inputs(inputs);
	}, [plans]);

	const handle_provider_change = (e) => {
		const provider = get_provider(e.target.value);
		set_tts_provider(e.target.value);
		if(!provider) {
			return;
		}
		if(provider.billing_mode === c_BillingModePerSecond) {
			set_cost_per_second_input(String(provider.default_cost_per_second));
			set_seconds_per_gen_input(String(provider.default_seconds_per_gen));
			return;
		}
		set_cost_per_gen_input(String(provider.default_cost_per_gen));
	};

	const update_plan = (id, field, value) => {
		const n = parseFloat(value);
		if(isNaN(n)) return;
		set_plan_inputs(function(prev) {
			return Object.assign({}, prev, {
				[id]: Object.assign({}, prev[id], { [field]: n })
			});
		});
	};

	const cost_per_call = (cost_per_gen * runs_per_call);

	const rows = Object.keys(plan_inputs).map(function(id) {
		const p = plan_inputs[id];
		const monthly_price = (p.billing_interval === 'year') ? (p.price / 12) : p.price;
		const revenue_per_credit = (p.credits > 0) && (monthly_price > 0) ? (monthly_price / p.credits) : 0;
		const revenue_per_call = (revenue_per_credit * runs_per_call);
		const profit_per_call = (revenue_per_call - cost_per_call);
		const margin = (revenue_per_call > 0) ? ((profit_per_call / revenue_per_call) * 100) : null;
		const revenue = (monthly_price * p.subscribers);
		const cogs = (cost_per_gen * p.credits * p.subscribers);
		const gp = (revenue - cogs);
		return { id, ...p, monthly_price, revenue_per_credit, revenue_per_call, profit_per_call, margin, revenue, cogs, gp };
	});

	const totals = rows.reduce(function(acc, r) {
		return { revenue: acc.revenue + r.revenue, cogs: acc.cogs + r.cogs, gp: acc.gp + r.gp };
	}, { revenue: 0, cogs: 0, gp: 0 });
	const total_margin = totals.revenue > 0 ? (totals.gp / totals.revenue) * 100 : null;

	const do_export = async () => {
		const data = JSON.stringify({
			model: 'per_generation',
			settings: { tts_provider, tts_provider_name, billing_mode, cost_per_second, seconds_per_gen, cost_per_gen, runs_per_call, cost_per_call },
			plans: rows,
			totals: Object.assign({}, totals, { margin: total_margin }),
		}, null, 2);
		try {
			const handle = await window.showSaveFilePicker({
				suggestedName: 'unit-economics-per-generation-' + tts_provider + '.json',
				types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
			});
			const writable = await handle.createWritable();
			await writable.write(data);
			await writable.close();
		} catch(e) {
			if(e.name !== 'AbortError') throw e;
		}
	};

	function render_input(label, value, on_change, step = '1') {
		return (
			<Form.Group className="mb-3">
				<Form.Label className="form-input-label">{label}</Form.Label>
				<Form.Control className="form-input" type="number" step={step} min="0" value={value} onChange={on_change} />
			</Form.Group>
		);
	}

	function render_cost_controls() {
		if(billing_mode === c_BillingModePerSecond) {
			return (
				<React.Fragment>
					<Col md={2}>
						{render_input('Cost per Second ($)', cost_per_second_input, function(e) { set_cost_per_second_input(e.target.value); }, '0.0001')}
					</Col>
					<Col md={2}>
						{render_input('Avg Seconds per Generation', seconds_per_gen_input, function(e) { set_seconds_per_gen_input(e.target.value); }, '0.1')}
					</Col>
					<Col md={2} className="d-flex align-items-center pt-2">
						<div>
							<div className="form-input-label mb-1">Cost per Generation</div>
							<div className="fw-bold" style={{ color: '#f5f5f5' }}>{'$' + fmt(cost_per_gen, 5)}</div>
						</div>
					</Col>
				</React.Fragment>
			);
		}

		return (
			<Col md={3}>
				{render_input('Cost per Voice Generation ($)', cost_per_gen_input, function(e) { set_cost_per_gen_input(e.target.value); }, '0.0001')}
			</Col>
		);
	}

	return (
		<Container fluid>
				<Card className="view-card mb-3">
					<Card.Body>
						<Card.Title className="card-title">TTS Cost Settings</Card.Title>
						<Row>
							<Col md={3}>
								<Form.Group className="mb-3">
									<Form.Label className="form-input-label">TTS Provider</Form.Label>
									<Form.Select className="form-input" value={tts_provider} onChange={handle_provider_change}>
										{TTS_PROVIDERS.map(function(p) {
											return <option key={p.value} value={p.value}>{p.label}</option>;
										})}
									</Form.Select>
								</Form.Group>
							</Col>
							{render_cost_controls()}
							<Col md={runs_per_call_column_width}>
								{render_input('TTS Runs per Call', runs_per_call_input, function(e) { set_runs_per_call_input(e.target.value); }, '1')}
							</Col>
							<Col md={3} className="d-flex align-items-center pt-2">
								<div>
									<div className="form-input-label mb-1">Cost per Call</div>
									<div className="fw-bold fs-5" style={{ color: '#f5f5f5' }}>{'$' + fmt(cost_per_call, 4)}</div>
								</div>
							</Col>
						</Row>
					</Card.Body>
				</Card>

				<Row>
					{rows.map(function(r) {
						const margin_color = r.margin === null ? '#f5f5f5' : r.margin < 0 ? '#dc3545' : r.margin < 50 ? '#ffc107' : '#28a745';
						return (
							<Col key={r.id} md={6} xl={4} className="mb-3">
								<Card className="view-card h-100">
									<Card.Body>
										<Card.Title className="card-title">
											{r.name}
											<span className="text-muted ms-2" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>
												({Constants.get_billing_interval_label(r.billing_interval)})
											</span>
										</Card.Title>
										<Row>
											<Col>
												{render_input(r.billing_interval === 'year' ? 'Annual Price ($)' : 'Monthly Price ($)', r.price, function(e) { update_plan(r.id, 'price', e.target.value); }, '0.01')}
											</Col>
											<Col>
												{render_input('Credits/mo', r.credits, function(e) { update_plan(r.id, 'credits', e.target.value); }, '1')}
											</Col>
										</Row>
										<Row>
											<Col>
												{render_input('Subscribers', r.subscribers, function(e) { update_plan(r.id, 'subscribers', e.target.value); }, '1')}
											</Col>
											<Col className="d-flex align-items-center pt-2">
												<div>
													<div className="form-input-label mb-1">Profit/Call</div>
													<div className="fw-bold" style={{ color: r.profit_per_call < 0 ? '#dc3545' : '#28a745' }}>
														{(r.profit_per_call > 0 ? '+' : '') + '$' + fmt(r.profit_per_call, 4)}
													</div>
													<div className="form-input-label mt-2 mb-1">Margin</div>
													<div className="fw-bold" style={{ color: margin_color }}>
														{r.margin !== null ? fmt(r.margin, 1) + '%' : '-'}
													</div>
												</div>
											</Col>
										</Row>
									</Card.Body>
								</Card>
							</Col>
						);
					})}
				</Row>

				<Card className="view-card mt-2">
					<Card.Body>
						<div className="d-flex justify-content-between align-items-center mb-3">
							<Card.Title className="card-title mb-0">Summary</Card.Title>
							<button className="btn btn-sm btn-outline-secondary" onClick={do_export}>Export JSON</button>
						</div>
						<Table bordered hover variant="dark">
							<thead>
								<tr>
									<th>Plan</th>
									<th>Billing</th>
									<th>Rev/Credit</th>
									<th>Cost/Call</th>
									<th>Profit/Call</th>
									<th>Margin</th>
									<th>Revenue</th>
									<th>COGS</th>
									<th>Gross Profit</th>
								</tr>
							</thead>
							<tbody>
								{rows.map(function(r) {
									return (
										<tr key={r.id}>
											<td>{r.name}</td>
											<td>{Constants.get_billing_interval_label(r.billing_interval)}</td>
											<td>{'$' + fmt(r.revenue_per_credit, 4)}</td>
											<td>{'$' + fmt(cost_per_call, 4)}</td>
											<td style={{ color: r.profit_per_call < 0 ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
												{(r.profit_per_call > 0 ? '+' : '') + '$' + fmt(r.profit_per_call, 4)}
											</td>
											<td style={{ color: r.margin === null ? '#f5f5f5' : r.margin < 0 ? '#dc3545' : r.margin < 50 ? '#ffc107' : '#28a745' }}>
												{r.margin !== null ? fmt(r.margin, 1) + '%' : '-'}
											</td>
											<td>{'$' + fmt(r.revenue)}</td>
											<td>{'$' + fmt(r.cogs)}</td>
											<td style={{ color: r.gp < 0 ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
												{(r.gp > 0 ? '+' : '') + '$' + fmt(r.gp)}
											</td>
										</tr>
									);
								})}
								<tr style={{ fontWeight: 'bold', backgroundColor: '#1a1a2e' }}>
									<td colSpan={6}>Total</td>
									<td>{'$' + fmt(totals.revenue)}</td>
									<td>{'$' + fmt(totals.cogs)}</td>
									<td style={{ color: totals.gp < 0 ? '#dc3545' : '#28a745' }}>
										{(totals.gp > 0 ? '+' : '') + '$' + fmt(totals.gp)}
									</td>
								</tr>
							</tbody>
						</Table>
					</Card.Body>
				</Card>
			</Container>
	);
}

function UnitEconomicsView() {
	const { token } = useAuth();
	const [plans, set_plans] = useState([]);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');

	useEffect(function() {
		const load = async () => {
			try {
				const headers = token ? { Authorization: 'Bearer ' + token } : {};
				const response = await axios.get(Constants.SERVER_URL + '/api/admin/plans_with_features', { headers });
				set_plans(response.data.plans || []);
			} catch(err) {
				set_error('Failed to load plans.');
			} finally {
				set_loading(false);
			}
		};
		load();
	}, [token]);

	if(loading) {
		return <div className="p-4"><Spinner animation="border" /></div>;
	}

	return (
		<main className="p-4">
			<h4 className="mb-4" style={{ color: '#f5f5f5' }}>Unit Economics</h4>
			{error && <Alert variant="danger">{error}</Alert>}
			<Tabs defaultActiveKey="per_gen" className="mb-4">
				<Tab eventKey="per_gen" title="Per Voice Generation (1 Credit = 1 AI Response)">
					<div className="pt-3">
						<PerGenerationModel plans={plans} />
					</div>
				</Tab>
			</Tabs>
		</main>
	);
}

export default UnitEconomicsView;
